import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";
import { Input } from "../../../components/DesignSystem/components/Input";
import { Button as DSButton } from "../../../components/DesignSystem/components/Button";
import { Chip } from "../../../components/DesignSystem/components/Chip";
import { ArrowLeft, ArrowRight, X, User, Users, Globe2, ScanFace, HeartHandshake, Martini} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as profileActions from "../../../actions/Profile";
import { API_URL } from "../../../api";
import { useCookies } from "../../../hooks/useCookies";
import { questionsDefaultAnswers } from '../../../utils/constants';
import ConfirmationModal from "../../../components/Utility/ConfirmationModal";
import QuestionPrompt from '../../../components/UpdateProfile/screens/EditAnswerAndQuestion/QuestionPrompt';
import BModal from 'react-bootstrap/Modal';
import { Spinner } from "react-bootstrap";
import { useAlert } from '../../../Context/AlertModalContext';
import './PersonalityModal.css';

function AlertModal({ show, onHide, count }) {
    return (
        <BModal className='customer-modal-without-header-footer' centered show={show} onHide={onHide}>
            <div>
                <p className="alert-modal-text text-center p-4">
                    {count !== 2 ? "Answer only 2 prompts to continue, please! Contact info (e.g., phone, social handles) will be rejected." : "Please answer only 2 questions"}
                </p>
            </div>
            <div className="alert-modal-button-container flex justify-center pb-4">
                <DSButton type="button" variant="cta" onClick={() => onHide()}>
                    Okay
                </DSButton>
            </div>
        </BModal>
    )
}

export const Personality = () => {
    const navigate = useNavigate();
    
    // 1. Theme Configuration Hook
    const theme = useTheme();
    const mode = theme.palette.mode;
    
    // 2. FOUC Tracker State
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(!!window.tailwind);

    // 3. Centralized FOUC Prevention & Engine Mount
    useEffect(() => {
        const applyConfig = () => {
            const modeColors = tokens(mode);
            if (window.tailwind) {
                window.tailwind.config = {
                    darkMode: "class",
                    theme: {
                        extend: {
                            colors: {
                                "brand": modeColors.greenAccent[500] || "#ff794d",
                                "background-light": mode === "dark" ? modeColors.primary[500] : "#f8f6f5",
                                "background-dark": modeColors.primary[500] || "#23140f",
                                "action-black": "#111111",
                            },
                            fontFamily: {
                                "display": ["Inter", "sans-serif"],
                                "body": ['"Source Sans Pro"', "sans-serif"],
                            },
                        },
                    },
                };
            }
        };

        if (window.tailwind) {
            applyConfig();
            setIsTailwindLoaded(true);
        } else {
            const script = document.createElement("script");
            script.src = "https://cdn.tailwindcss.com?plugins=forms";
            script.onload = () => {
                applyConfig();
                setIsTailwindLoaded(true);
            };
            document.head.appendChild(script);
        }
    }, [mode]);

    const { getCookie } = useCookies();
    const token = getCookie("token");
    const userVerified = getCookie("verified");
    const alert = useAlert();

    const [loading, setLoading] = useState(false);
    
    // Personality Traits State
    const [personalities, setPersonalities] = useState([]);
    const [selectedPersonalities, setSelectedPersonalities] = useState([]);
    const [traitsSearch, setTraitsSearch] = useState("");
    const [activeTraitGroup, setActiveTraitGroup] = useState("All");
    
    // Profile Prompts State
    const [questions, setQuestions] = useState([]);
    const [questionsCount, setQuestionsCount] = useState(0);
    const [showAnswerModal, setShowAnswerModal] = useState(false);
    const [answerModalData, setAnswerModalData] = useState({});
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteQuestionId, setDeleteQuestionId] = useState(null);
    const [refreshQuestions, setRefreshQuestions] = useState(false);
    
    const [isPromptSelectionOpen, setIsPromptSelectionOpen] = useState(false);

    // Logic fetching
    const isFetched = useRef(false);

    const fetchPersonalityOptions = useCallback(() => {
        fetch(`${API_URL}customer/users/personality-options`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.personalities) {
                setPersonalities(data.personalities);
            }
        })
        .catch((error) => console.error("Error fetching personality options:", error));
    }, [token]);

    const fetchPersonalities = useCallback(() => {
        fetch(`${API_URL}customer/users/personalities`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.personalities) {
                setSelectedPersonalities(data.personalities);
            }
        })
        .catch((error) => console.error("Error fetching user personalities:", error));
    }, [token]);

    const fetchQuestionsList = useCallback(async () => {
        const response = await profileActions.questionAnswersList();
        if (response) {
            setQuestions(response.questions);
        }
    }, []);

    useEffect(() => {
        if (isFetched.current) return;
        isFetched.current = true;

        if (!token) return;
        fetchPersonalityOptions();
        fetchPersonalities();
        fetchQuestionsList();
    }, [token, fetchPersonalityOptions, fetchPersonalities, fetchQuestionsList]);

    useEffect(() => {
        profileActions.getQuestionsCount().then(res => setQuestionsCount(res.count));
    }, [refreshQuestions]);

    const handlePersonalityClick = (personality) => {
        const isAlreadySelected = selectedPersonalities.find((p) => p.name === personality.name);
        if (isAlreadySelected) {
            setSelectedPersonalities(selectedPersonalities.filter((p) => p.name !== personality.name));
        } else {
            if (selectedPersonalities?.length >= 8) {
                alert?.setModal?.({
                    title: "",
                    message: "You cannot select more than 8 options.",
                    show: true,
                });
                return;
            }
            setSelectedPersonalities([...selectedPersonalities, personality]);
        }
    };

    const handleDeleteAnswer = (questionId) => {
        setDeleteQuestionId(questionId);
        setShowDeleteConfirmation(true);
    };

    const confirmDeleteAnswer = async () => {
        try {
            // Prevent server crash: If a user has a cached 'custom-' ID, just remove it from local state.
            if (String(deleteQuestionId).startsWith('custom-')) {
                setQuestions(prev => prev.filter(q => q.question_id !== deleteQuestionId));
                setRefreshQuestions(prev => !prev);
                setShowDeleteConfirmation(false);
                setDeleteQuestionId(null);
                return;
            }

            const response = await profileActions.deleteQuestionAnswer(deleteQuestionId);
            if (response) {
                setQuestions(prev => prev.map(q => q.question_id === deleteQuestionId ? { ...q, answer: "" } : q));
                setRefreshQuestions(prev => !prev);
            }
            setShowDeleteConfirmation(false);
            setDeleteQuestionId(null);
        } catch (error) {
            console.error(error);
            setShowDeleteConfirmation(false);
        }
    };

    const handleAnswerSave = async (questionId, newAnswer) => {
        setLoading(true);
        try {
            // For custom prompts created on-the-fly, stringify or fallback to post if needed
            // Currently utilizing standard endpoint, backend behavior dictates success
            const response = await profileActions.saveQuestionAnswer(questionId, newAnswer);
            if (response) {
                setQuestions(prev => {
                    // Check if question existed, if not append it
                    const existing = prev.find(q => q.question_id === questionId);
                    if (existing) {
                        return prev.map(q => q.question_id === questionId ? { ...q, answer: newAnswer } : q);
                    } else {
                        // Optimistically push the custom prompt
                        return [...prev, { question_id: questionId, question: answerModalData.heading, answer: newAnswer }];
                    }
                });
                setRefreshQuestions(prev => !prev);
                setShowAnswerModal(false);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Dynamic semantic grouping for Traits using keyword matching
    const getFallbackCat = (p) => {
        if (!p || !p.name) return "Other";
        const n = p.name.toLowerCase();
        
        // 1. Social Energy & Dynamics
        if (n.match(/introvert|extrovert|ambivert|social|shy|outgoing|party|homebody|talkative|quiet|listener|observer/)) return "Social";

        // 2. Travel & Adventure
        if (n.match(/travel|advanture|adventur|wanderlust|explore|globetrotter|backpack|nomad|outdoor|camping|hiking|beach|mountain|nature/)) return "Travel";

        // 3. Food & Drink
        if (n.match(/food|drink|coffee|tea|wine|beer|cook|bake|vegan|vegetarian|restaurant|dining|cocktail|foodie/)) return "Food";

        // 4. Fitness & Health
        if (n.match(/fit|workout|gym|run|yoga|health|athlete|sport|active|lift|dance|swim|cycle/)) return "Fitness";

        // 5. Arts & Culture
        if (n.match(/art|music|paint|draw|museum|culture|book|read|write|theater|movie|film|cinema|create|design/)) return "Culture";

        // 6. Career & Ambition
        if (n.match(/work|ambition|career|study|student|professional|entrepreneur|business|drive|hustle|goal|lead/)) return "Career";

        // 7. Pets & Animals
        if (n.match(/pet|dog|cat|animal|bird|fish|equine|horse/)) return "Pets";

        // 8. Core Values & Emotion
        if (n.match(/honest|kind|loyal|funny|humor|smart|intelligent|curious|empathetic|spiritual|faith|religion|romantic|affection|patient|open/)) return "Values";
        
        // 9. Hobbies & Interests (Catch-all for fun stuff)
        if (n.match(/game|gamer|tech|nerd|geek|fashion|style|shopping|car|motor|boardgame/)) return "Hobbies";

        return "Personality";
    };

    const traitGroups = React.useMemo(() => {
        const groups = new Set(personalities.map(p => p.category || p.group || p.type || getFallbackCat(p)).filter(Boolean));
        return Array.from(groups).sort();
    }, [personalities]);

    const [expandedCategory, setExpandedCategory] = useState(null);
    const toggleCategory = (cat) => setExpandedCategory(expandedCategory === cat ? null : cat);

    // Dynamic grouping for Prompts using keyword matching
    const getPromptCategory = (q) => {
        const text = (q.prompt || q.question || "").toLowerCase();
        if (text.match(/date|romantic|love|relationship|kiss|partner|crush|flirt/)) return "Dating & Romance";
        if (text.match(/weekend|sunday|friday|saturday|free time|hobby|fun|weekend|day off/)) return "Free Time";
        if (text.match(/story|embarrassing|memory|childhood|child|kid|school|funny/)) return "Stories & Memories";
        if (text.match(/work|job|career|boss|money|dream|success|goal/)) return "Career & Ambition";
        if (text.match(/pet|dog|cat|animal/)) return "Pets";
        if (text.match(/food|eat|cook|meal|restaurant|pizza|dinner|breakfast/)) return "Food & Drink";
        if (text.match(/travel|country|place|vacation|trip|flight|world/)) return "Travel";
        if (text.match(/music|movie|song|art|book|read|show|tv|concert/)) return "Arts & Culture";
        if (text.match(/belief|value|religion|god|opinion|believe|life/)) return "Values";
        return "Personality & Quirks";
    };

    // Prompts Display Logic
    const answeredPrompts = questions.filter(q => q.answer && q.answer.trim() !== '');
    const unansweredPrompts = questions.filter(q => !q.answer || q.answer.trim() === '');

    const promptCategoriesGroup = React.useMemo(() => {
        const categories = new Set(unansweredPrompts.map(p => getPromptCategory(p)));
        return ["All", ...Array.from(categories).sort()];
    }, [unansweredPrompts]);

    const [activePromptCategory, setActivePromptCategory] = useState("All");

    // Exact requirement: "Select 2" -> provide exactly 2 slots visually. 
    // Hinge-like pattern guarantees space visualization
    const promptSlots = [...answeredPrompts];
    while (promptSlots.length < 2) {
        promptSlots.push(null);
    }

    const openPromptSelection = () => setIsPromptSelectionOpen(true);
    
    const handleSelectUnansweredPrompt = (question) => {
        setIsPromptSelectionOpen(false);
        setAnswerModalData({
            question,
            heading: question.prompt || question.question,
            refreshpage: () => setRefreshQuestions(prev => !prev),
            questionId: question.question_id,
            onAnswerSaved: handleAnswerSave,
            placeholder: "Type your answer...",
            answer: "",
        });
        setTimeout(() => setShowAnswerModal(true), 150);
    };

    const handleSaveAll = async () => {
        if (selectedPersonalities?.length < 3 || selectedPersonalities?.length > 8) {
            alert?.setModal?.({ title: "", message: "Please select between 3 and 8 personalities.", show: true });
            return;
        }

        if (questionsCount < 2) {
            setShowAlertModal(true);
            return;
        }

        setLoading(true);

        // Navigate instantly for a smooth UX
        navigate(userVerified ? '/user/home' : '/approve');

        try {
            fetch(`${API_URL}customer/users/personality`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ personalities: selectedPersonalities.map(p => p.id) })
            }).catch((error) => console.error("Error saving personalities in background:", error));
        } catch (e) {
            console.error("Error executing update logic:", e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div 
            className={`${mode === "dark" ? "dark" : ""} bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 antialiased min-h-[100dvh] lg:h-screen lg:overflow-hidden w-full`}
            style={{ opacity: isTailwindLoaded ? 1 : 0, transition: 'opacity 0.1s ease-out' }}
        >
            <div className="flex flex-col min-h-[100dvh] lg:h-screen w-full overflow-hidden bg-white dark:bg-background-dark">
                {/* Main Content Area */}
                <div className="w-full flex-1 flex flex-col min-h-[100dvh] lg:h-screen overflow-y-auto custom-scrollbar relative">
                    
                    {/* Header/Nav */}
                    <header className="sticky top-0 z-30 bg-background-light/90 dark:bg-background-dark/90 backdrop-blur-md px-[var(--ds-space-6)] py-[var(--ds-space-4)] flex items-center justify-between border-b border-brand/10">
                        <button type="button" onClick={() => navigate(-1)} className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Go back">
                            <ArrowLeft className="shrink-0" size={24} />
                        </button>
                        <div className="flex items-center gap-[var(--ds-space-2)]">
                            <div className="h-2 w-24 bg-brand/20 rounded-full overflow-hidden">
                                <div className="h-full bg-brand" style={{ width: '100%' }}></div>
                            </div>
                            <span className="text-xs font-bold uppercase tracking-wider text-brand">Step 6 of 6</span>
                        </div>
                        <button type="button" className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brand/10 text-action-black dark:text-white hover:bg-brand/20 transition-colors" aria-label="Close">
                            <X className="shrink-0" size={24} />
                        </button>
                    </header>

                    <main className="flex-1 px-[var(--ds-space-6)] py-[var(--ds-space-6)] lg:px-[var(--ds-space-12)] lg:py-[var(--ds-space-8)] max-w-6xl mx-auto w-full flex flex-col items-start text-left pb-40 lg:pb-[var(--ds-space-8)]">
                        <div className="mb-[var(--ds-space-8)] w-full text-left">
                            <Heading variant="h1" className="ds-heading-page-title text-slate-900 dark:text-slate-100 mb-[var(--ds-space-2)] text-left">
                                Show your personality
                            </Heading>
                            <Text as="p" className="ds-text-page-subtitle text-slate-500 dark:text-slate-400 text-left">
                                Let others get to know the real you through your traits and stories.
                            </Text>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-[var(--ds-space-6)] pb-10 flex-1 w-full max-w-2xl lg:max-w-none mx-auto lg:items-start">
                            {/* --- LEFT COLUMN: Traits --- */}
                            <div className="flex-1 w-full flex flex-col gap-[var(--ds-space-6)]">
                                <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 transition-all flex flex-col h-full">
                                    <Heading variant="h4" className="text-lg font-bold flex items-center gap-2 mb-4 text-slate-900 dark:text-slate-100">
                                        <span className="material-symbols-outlined text-brand">auto_awesome</span>
                                        Your Traits
                                    </Heading>
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Select 3-8</span>
                                    
                                    {/* Traits Search Removed for Mobile UX */}

                                    {/* Selected Traits Session at Top */}
                                    {selectedPersonalities.length > 0 && (
                                        <div className="mb-4 bg-brand/5 border border-brand/20 p-4 rounded-2xl shrink-0">
                                            <p className="text-xs font-bold text-brand uppercase tracking-widest mb-3">Selected ({selectedPersonalities.length}/8)</p>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedPersonalities.map(p => (
                                                    <button 
                                                        key={`sel-${p.id}`}
                                                        onClick={() => handlePersonalityClick(p)}
                                                        className="px-3 py-1.5 rounded-full bg-brand text-white text-sm font-semibold flex items-center gap-1.5 shadow-sm hover:bg-brand/90 transition-colors"
                                                    >
                                                        {p.name} <X size={14} className="opacity-80"/>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Unified Inline Chips Group */}
                                    <div className="flex flex-wrap gap-2 mb-4 shrink-0">
                                        {traitGroups.map(grp => {
                                            if (grp === "All") return null;
                                            
                                            // Check if group contains traits
                                            const groupTraits = personalities.filter(p => {
                                                const cat = p.category || p.group || p.type || getFallbackCat(p);
                                                return cat === grp;
                                            });
                                            if (groupTraits.length === 0) return null;

                                            const isExpanded = expandedCategory === grp;
                                            
                                            return (
                                                <React.Fragment key={grp}>
                                                    {/* Category Chip */}
                                                    <button 
                                                        onClick={() => toggleCategory(grp)}
                                                        className={`px-4 py-2 rounded-full border text-[13px] md:text-sm font-semibold transition-all focus:outline-none ${
                                                            isExpanded 
                                                            ? "bg-slate-800 border-transparent text-white dark:bg-slate-200 dark:text-slate-900 shadow-md ring-1 ring-slate-800 dark:ring-slate-200"
                                                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 dark:bg-slate-900/50 dark:text-slate-400 dark:border-slate-700 hover:border-slate-300 shadow-sm"
                                                        }`}
                                                    >
                                                        {grp}
                                                    </button>

                                                    {/* Inline Expanded Traits immediately adjacent */}
                                                    {isExpanded && groupTraits.map(personality => {
                                                        const isSelected = selectedPersonalities.find(p => p.id === personality.id);
                                                        return (
                                                            <button 
                                                                key={personality.id}
                                                                onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handlePersonalityClick(personality);
                                                                }}
                                                                className={`px-3 py-1.5 rounded-full border text-[13px] md:text-sm transition-all focus:outline-none animate-in fade-in duration-200 ${
                                                                    isSelected 
                                                                    ? 'border-brand bg-brand/5 text-brand font-semibold shadow-sm ring-1 ring-brand' 
                                                                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 bg-slate-50 dark:bg-slate-800/60 font-medium text-slate-600 dark:text-slate-300'
                                                                }`}
                                                            >
                                                                {personality.name}
                                                            </button>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* --- RIGHT COLUMN: Prompts --- */}
                            <div className="flex-1 w-full flex flex-col gap-[var(--ds-space-6)]">
                                <div className="bg-slate-50 dark:bg-slate-800/30 p-4 md:p-6 rounded-2xl border border-slate-200 dark:border-slate-700/80 transition-all flex flex-col h-full">
                                    <Heading variant="h4" className="text-lg font-bold flex items-center gap-2 mb-4 text-slate-900 dark:text-slate-100">
                                        <span className="material-symbols-outlined text-brand">chat_bubble</span>
                                        Profile Prompts
                                    </Heading>
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Select Exactly 2</span>
                                    
                                    <div className="flex flex-col gap-3">
                                        {promptSlots.map((prompt, index) => {
                                            if (!prompt) {
                                                // Empty Slot rendering block
                                                return (
                                                    <div 
                                                        key={`empty-${index}`}
                                                        onClick={() => {
                                                            if (answeredPrompts.length < 2) {
                                                                openPromptSelection();
                                                            } else {
                                                                setShowAlertModal(true);
                                                            }
                                                        }}
                                                        className={`flex items-center justify-between p-5 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 transition-colors ${
                                                            answeredPrompts.length < 2 
                                                            ? "cursor-pointer hover:border-brand/60 hover:bg-slate-100 dark:hover:bg-slate-800/60" 
                                                            : "opacity-50 cursor-not-allowed"
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                                                            <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center shrink-0 text-slate-400">
                                                                <span className="material-symbols-outlined text-sm">add</span>
                                                            </div>
                                                            <span className="font-medium text-sm">Add a prompt...</span>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            // Answered Slot rendering block
                                            return (
                                                <div 
                                                    key={prompt.question_id}
                                                    onClick={() => {
                                                        setAnswerModalData({
                                                            question: prompt,
                                                            heading: prompt.prompt || prompt.question,
                                                            refreshpage: () => setRefreshQuestions(prev => !prev),
                                                            questionId: prompt.question_id,
                                                            onAnswerSaved: handleAnswerSave,
                                                            placeholder: "Type your answer...",
                                                            answer: prompt.answer,
                                                        });
                                                        setShowAnswerModal(true);
                                                    }}
                                                    className="group relative p-5 rounded-xl bg-white dark:bg-slate-900/50 border shadow-sm border-brand/20 dark:border-brand/20 transition-colors cursor-pointer hover:shadow-md hover:border-brand/40"
                                                >
                                                    <div className="flex flex-col">
                                                        <p className="text-[11px] font-bold text-brand uppercase tracking-tighter mb-2 pr-12">{prompt.question}</p>
                                                        <p className="text-slate-900 dark:text-slate-100 font-medium break-words leading-relaxed text-sm">"{prompt.answer}"</p>
                                                        
                                                        <div className="absolute top-4 right-4 flex gap-1">
                                                            <button 
                                                                className="text-slate-300 hover:text-brand transition-colors p-1"
                                                            >
                                                                <span className="material-symbols-outlined text-sm">edit</span>
                                                            </button>
                                                            <button 
                                                                className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteAnswer(prompt.question_id);
                                                                }}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </main>

                    {/* Fixed Sticky Footer for CTAs */}
                    <div className="fixed bottom-0 left-0 right-0 bg-background-light/90 dark:bg-background-dark/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 p-4 lg:p-6 z-50 w-full lg:static lg:bg-transparent lg:backdrop-blur-none lg:border-none lg:mt-auto">
                        <div className="flex flex-col md:flex-row justify-center items-center gap-4 max-w-6xl mx-auto">
                            <DSButton 
                                type="button"
                                variant="secondary"
                                onClick={() => navigate(userVerified ? '/user/home' : '/approve')}
                                disabled={loading}
                                className="w-full md:w-[220px]"
                            >
                                Skip for now
                            </DSButton>
                            <DSButton 
                                type="button"
                                variant="cta"
                                onClick={handleSaveAll}                            
                                disabled={loading}
                                className="w-full md:w-[220px]"
                            >
                                {loading ? <Spinner animation="border" size="sm" /> : "Complete Setup"}
                            </DSButton>
                        </div>
                    </div>
                </div>
            </div>

            {/* Prompt Selection Modal */}
            <BModal 
                centered 
                show={isPromptSelectionOpen} 
                onHide={() => setIsPromptSelectionOpen(false)} 
                className="personality-prompt-selection-modal customer-modal-without-header-footer z-[9999]"
                dialogClassName="personality-prompt-selection-modal-dialog"
            >
                <div className="prompt-modal-inner-wrapper flex flex-col h-full sm:h-auto sm:max-h-[85vh] sm:rounded-3xl border border-slate-100 dark:border-slate-800 shadow-2xl">
                    <div className="prompt-modal-header p-6 md:p-8 pb-4 shrink-0 bg-background-light dark:bg-background-dark/95 border-b border-slate-100 dark:border-slate-800/50">
                        <div className="flex justify-between items-center mb-4">
                            <Heading variant="h3" className="text-xl md:text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100">Choose a Prompt</Heading>
                            <button onClick={() => setIsPromptSelectionOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-brand transition-all">
                                <X size={20} strokeWidth={3} />
                            </button>
                        </div>
                        <Text as="p" className="text-sm md:text-base text-slate-500 font-medium mb-6">Pick a question to share your unique story.</Text>
                        
                        {/* Search Bar - Full Width Row */}
                        <div className="relative w-full mb-6 max-w-none">
                            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-brand font-bold">search</span>
                            <input 
                                type="text" 
                                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl py-3 pl-12 pr-4 text-sm font-semibold focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 placeholder:text-slate-400 shadow-sm transition-all"
                                placeholder="Search prompts by keyword..." 
                                onChange={(e) => {
                                    const val = e.target.value.toLowerCase();
                                    document.querySelectorAll('.prompt-list-item').forEach(el => {
                                        if (el.dataset.searchable) {
                                            el.style.display = el.textContent.toLowerCase().includes(val) ? 'flex' : 'none';
                                        }
                                    });
                                }}
                            />
                        </div>

                        {/* Category Row - Full Width Scrollable Row */}
                        <div className="flex items-center overflow-x-auto custom-scrollbar gap-2 shrink-0 no-scrollbar pb-2 w-full max-w-none">
                            {promptCategoriesGroup.map(cat => (
                                <button 
                                    key={cat}
                                    onClick={() => setActivePromptCategory(cat)}
                                    className={`px-5 py-2.5 rounded-xl text-xs md:text-sm font-bold whitespace-nowrap transition-all border ${
                                        activePromptCategory === cat 
                                        ? "bg-brand text-white border-transparent shadow-lg shadow-brand/20 scale-105"
                                        : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50 dark:bg-slate-900/40 dark:border-slate-700 dark:hover:bg-slate-800"
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="prompt-modal-body flex-1 overflow-y-auto custom-scrollbar p-6 md:p-8 pt-2 pr-2 md:pr-4">
                        {/* Custom Prompts Temporarily Disabled: The backend requires predefined integer question IDs. */}

                        {/* Dense grid for prompt tags */}
                        <div className="prompt-modal-grid grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
                            {unansweredPrompts
                                .filter(q => activePromptCategory === "All" || getPromptCategory(q) === activePromptCategory)
                                .map((q) => (
                                <div 
                                    key={q.question_id}
                                    data-searchable="true"
                                    onClick={() => handleSelectUnansweredPrompt(q)}
                                    className="prompt-list-item px-4 py-3 md:p-5 rounded-xl md:rounded-2xl cursor-pointer transition-all bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/80 hover:border-brand/50 hover:bg-white dark:hover:bg-slate-800 hover:shadow-md break-inside-avoid relative group flex items-start justify-between gap-3"
                                >
                                    <span className="font-medium text-slate-700 dark:text-slate-300 leading-snug text-sm md:text-base">
                                        "{q.question}"
                                    </span>
                                    <div className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity shrink-0">
                                        <div className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center shadow-sm">
                                            <span className="material-symbols-outlined text-[14px]">add</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {unansweredPrompts.length === 0 && (
                            <div className="w-full text-center py-12 flex flex-col items-center">
                                <span className="material-symbols-outlined text-5xl text-slate-200 dark:text-slate-700 mb-3 block">inventory_2</span>
                                <p className="text-slate-500 font-semibold text-sm">No more prompts available to answer.</p>
                            </div>
                        )}
                        {unansweredPrompts.filter(q => activePromptCategory === "All" || getPromptCategory(q) === activePromptCategory).length === 0 && unansweredPrompts.length > 0 && (
                            <div className="w-full text-center py-12 flex flex-col items-center col-span-2">
                                <span className="material-symbols-outlined text-5xl text-slate-200 dark:text-slate-700 mb-3 block">search_off</span>
                                <p className="text-slate-500 font-semibold text-sm">No prompts found in "{activePromptCategory}".</p>
                            </div>
                        )}
                    </div>
                </div>
            </BModal>

            <QuestionPrompt
                show={showAnswerModal}
                modalData={answerModalData}
                onSave={handleAnswerSave}
                onHide={() => setShowAnswerModal(false)}
            />
            <AlertModal show={showAlertModal} count={questionsCount} onHide={() => setShowAlertModal(false)} />
            <ConfirmationModal
                show={showDeleteConfirmation}
                onHide={() => setShowDeleteConfirmation(false)}
                title="Delete Answer"
                message="Are you sure you want to delete this answer? This action cannot be undone."
                confirmLabel="Delete"
                confirmVariant="danger"
                cancelLabel="Cancel"
                onConfirm={confirmDeleteAnswer}
            />

            {/* Inject minimal scrollbar styles for this component */}
            <style dangerouslySetInnerHTML={{__html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #ff794d33; border-radius: 10px; }
            `}} />
        </div>
    );
};
